import { useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { z } from 'zod'
import { useResetPassword } from '@zendia/frontend-shared'
import { axiosRequest } from '../../utilities/axiosClient'
import { OnboardingLayout } from '../../screens/onboarding/components'
import { Alert, AlertVariants } from '../../components/Alert'
import { Button, ButtonVariants } from '../../components/Button'
import { Input } from '../../components/Input'

export const Route = createFileRoute('/_unauthenticated/reset-password')({
  component: ResetPasswordComponent,
  validateSearch: z.object({
    // Comes from the emailed link. Absent means the user reached this screen some other
    // way, which the component reports rather than failing to validate.
    token: z.string().optional().catch(''),
  }),
})

const MINIMUM_PASSWORD_LENGTH = 8

function ResetPasswordComponent() {
  const { token } = Route.useSearch()
  const navigate = useNavigate()
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [errors, setErrors] = useState<{ password?: string; confirmPassword?: string }>({})
  const resetPassword = useResetPassword(axiosRequest)

  const resetError = resetPassword.isError
    ? ((resetPassword.error as { error?: { detail?: string } })?.error?.detail ??
      'We couldn’t reset your password. Please request a new link.')
    : null

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const nextErrors: { password?: string; confirmPassword?: string } = {}
    if (password.length < MINIMUM_PASSWORD_LENGTH) {
      nextErrors.password = `Password must be at least ${MINIMUM_PASSWORD_LENGTH} characters`
    }
    if (password !== confirmPassword) {
      nextErrors.confirmPassword = 'Passwords do not match'
    }
    setErrors(nextErrors)
    if (Object.keys(nextErrors).length > 0) return

    try {
      await resetPassword.mutateAsync({ token: token ?? '', password })
      // The password has changed and every existing session was revoked, so the user has
      // to sign in again - which is also the confirmation that it worked.
      navigate({
        to: '/login',
        search: {
          notice: 'Your password has been reset. Please sign in with your new password.',
        },
      })
    } catch {
      // Surfaced through resetError above
    }
  }

  if (!token) {
    return (
      <OnboardingLayout title="Reset Your Password">
        <Alert type={AlertVariants.error} compact>
          <div className="w-full text-center">
            This password reset link is incomplete. Please request a new one.
          </div>
        </Alert>
        <Button
          onClick={() => navigate({ to: '/forgot-password' })}
          intent={ButtonVariants.primary}
          size={ButtonVariants.large}
          className="w-full mt-6"
        >
          Request a New Link
        </Button>
      </OnboardingLayout>
    )
  }

  return (
    <OnboardingLayout
      title="Choose a New Password"
      description="Set a new password for your account."
    >
      <form onSubmit={handleSubmit} className="flex flex-col space-y-4">
        <Input
          id="password"
          label="New Password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Enter a secure password"
          disabled={resetPassword.isPending}
          error={errors.password}
          autoComplete="new-password"
        />
        <Input
          id="confirm-password"
          label="Confirm New Password"
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          placeholder="Confirm your password"
          disabled={resetPassword.isPending}
          error={errors.confirmPassword}
          autoComplete="new-password"
        />

        {resetError && (
          <div className="text-negative-700 dark:text-negative-500 text-sm py-1 px-3">
            {resetError}
          </div>
        )}

        <Button
          type="submit"
          intent={ButtonVariants.primary}
          size={ButtonVariants.large}
          className="w-full"
          disabled={resetPassword.isPending}
        >
          {resetPassword.isPending ? 'Saving...' : 'Set New Password'}
        </Button>

        <div className="text-center">
          <Button
            onClick={() => navigate({ to: '/forgot-password' })}
            intent={ButtonVariants.accent}
            action={ButtonVariants.link}
            size={ButtonVariants.inlineLink}
          >
            Request a new link
          </Button>
        </div>
      </form>
    </OnboardingLayout>
  )
}
